import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import Footer from '../../components/HeaderFooter/Footer';
import { useTheme } from '../../Context/themeContext';

const HelpSection = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [activeFaq, setActiveFaq] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  function handleClick(navigation) {
    if (navigation === 'contact') navigate('/contact');
  }

  // Theme-aware classes
  const isDark = theme === 'dark';
  
  const bgPrimary = isDark ? 'bg-[#0B1120]' : 'bg-gray-50';
  const bgCard = isDark ? 'bg-[#1E293B]' : 'bg-white';
  const bgCardHover = isDark ? 'hover:bg-blue-900/20' : 'hover:bg-blue-50/50';
  const bgFaq = isDark ? 'bg-[#0F172A]' : 'bg-gray-100';
  const borderColor = isDark ? 'border-blue-900/30' : 'border-gray-200';
  const borderHover = isDark ? 'hover:border-blue-700/50' : 'hover:border-blue-400';
  const shadowColor = isDark ? 'shadow-blue-900/20' : 'shadow-blue-200/50';
  
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textSecondary = isDark ? 'text-blue-200' : 'text-gray-700';
  const textMuted = isDark ? 'text-blue-200/70' : 'text-gray-600';
  //const textLight = isDark ? 'text-blue-300' : 'text-gray-600';
  const textFaq = isDark ? 'text-blue-200/70' : 'text-gray-600';
  
  const statColor = isDark ? 'text-white' : 'text-gray-900';
  const statLabel = isDark ? 'text-blue-400' : 'text-blue-600';
  const accentColor = isDark ? 'text-blue-400' : 'text-blue-600';
  
  const gradientOverlay = isDark 
    ? 'bg-gradient-to-t from-blue-900/80 to-transparent'
    : 'bg-gradient-to-t from-blue-400/60 to-transparent';

  const faqs = [
    {
      question: "How do I install the biometric system?",
      answer: "Our team provides professional installation services. We schedule an on-site visit to install and configure all devices. The process takes 2-3 hours with full training provided."
    },
    {
      question: "Can I integrate with payroll systems?",
      answer: "Yes, our software integrates with popular payroll systems including QuickBooks, Zoho, and custom solutions via API. Integration typically takes 1-2 days."
    },
    {
      question: "What support do you offer after purchase?",
      answer: "We offer 24/7 technical support via phone and email, regular software updates, remote troubleshooting, and on-site support for hardware issues."
    },
    {
      question: "Is my employee data secure?",
      answer: "Yes, we use enterprise-grade encryption, regular security audits, GDPR compliance, and ISO 27001 certified data centers for maximum security."
    },
    {
      question: "How long does implementation take?",
      answer: "Basic setup takes 1-2 weeks. Full customization with integrations may take 3-4 weeks. We work closely with your team throughout."
    },
    {
      question: "Do you provide training for staff?",
      answer: "Yes, we provide comprehensive training for administrators and end-users, including documentation, video tutorials, and on-site sessions."
    }
  ];

  // Loading spinner component
  const LoadingSpinner = () => (
    <div className={`min-h-screen ${bgPrimary} flex items-center justify-center transition-colors duration-300`}>
      <div className="text-center">
        <div className="relative">
          <div className={`w-16 h-16 border-4 rounded-full animate-spin ${isDark ? 'border-blue-900/30 border-t-blue-400' : 'border-gray-200 border-t-blue-600'}`}></div>
          <div className="absolute transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2">
            <div className={`w-3 h-3 rounded-full animate-pulse ${isDark ? 'bg-blue-400' : 'bg-blue-600'}`}></div>
          </div>
        </div>
        <p className={`mt-4 animate-pulse ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>Loading help center...</p>
      </div>
    </div>
  );

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className={`min-h-screen ${bgPrimary} py-28 px-4 sm:px-6 lg:px-8 transition-colors duration-300`}>
      <div className="mx-auto max-w-7xl">
        {/* Page title with fade-in animation */}
        <div className="mb-8 text-center animate-fadeIn">
          <h1 className={`text-4xl font-bold ${textPrimary} mb-2`}>
            How Can We <span className={accentColor}>Help You?</span>
          </h1>
          <p className={textSecondary}>Get answers to common questions or reach out to our support team</p>
        </div>

        <div className="grid items-start grid-cols-1 gap-12 lg:grid-cols-2">
          
          {/* Left Side - Image and Text */}
          <div 
            className="space-y-6 animate-slideInLeft"
            style={{ animationDelay: '0.2s' }}
          >
            {/* Image with overlay */}
            <div className="relative overflow-hidden group rounded-2xl">
              <img 
                src="https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                alt="Support team"
                className="object-cover w-full h-64 transition-transform duration-700 transform rounded-2xl group-hover:scale-105"
              />
              <div className={`absolute inset-0 ${gradientOverlay}`}></div>
              
              {/* Floating badge */}
              <div className="absolute px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded-full top-4 left-4 animate-bounce">
                24/7 Support
              </div>
            </div>

            {/* Text content */}
            <div className={`${bgCard} p-6 rounded-2xl border ${borderColor} transition-colors duration-300`}>
              <h2 className={`text-2xl font-bold ${textPrimary} mb-3`}>
                We're Here to <span className={accentColor}>Help</span>
              </h2>
              <p className={`${textMuted} mb-4`}>
                Our dedicated support team is available around the clock to assist you with any questions or issues. Whether you need technical support, training, or just some advice, we're just a message away.
              </p>
              
              {/* Contact options */}
              <div className="grid grid-cols-2 gap-3 mt-6">
                <div className={`flex items-center space-x-2 ${isDark ? 'text-blue-300' : 'text-gray-700'}`}>
                  <svg className={`w-5 h-5 ${accentColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span className="text-sm">+91 9390952388</span>
                </div>
                <div className={`flex items-center space-x-2 ${isDark ? 'text-blue-300' : 'text-gray-700'}`}>
                  <svg className={`w-5 h-5 ${accentColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm">sales@bandymoot.com</span>
                </div>
              </div>

              {/* Stats */}
              <div className={`grid grid-cols-3 gap-4 mt-6 pt-6 border-t ${isDark ? 'border-blue-900/30' : 'border-gray-200'}`}>
                <div>
                  <div className={`text-2xl font-bold ${statColor}`}>24/7</div>
                  <div className={`text-xs ${statLabel}`}>Support</div>
                </div>
                <div>
                  <div className={`text-2xl font-bold ${statColor}`}>15min</div>
                  <div className={`text-xs ${statLabel}`}>Avg response</div>
                </div>
                <div>
                  <div className={`text-2xl font-bold ${statColor}`}>98%</div>
                  <div className={`text-xs ${statLabel}`}>Satisfaction</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - FAQs with Animation */}
          <div 
            className={`${bgCard} p-6 rounded-2xl border ${borderColor} animate-slideInRight transition-colors duration-300`}
            style={{ animationDelay: '0.4s' }}
          >
            <h2 className={`text-2xl font-bold ${textPrimary} mb-6`}>
              Frequently Asked <span className={accentColor}>Questions</span>
            </h2>

            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className={`border ${borderColor} rounded-lg overflow-hidden transform transition-all duration-300 ${borderHover} hover:shadow-lg ${shadowColor}`}
                  style={{ animationDelay: `${0.5 + index * 0.1}s` }}
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className={`w-full px-4 py-3 text-left flex justify-between items-center ${bgFaq} ${bgCardHover} transition-colors duration-300`}
                  >
                    <span className={`${textPrimary} font-medium text-sm pr-8`}>{faq.question}</span>
                    <svg
                      className={`w-5 h-5 ${accentColor} flex-shrink-0 transform transition-all duration-300 ${
                        activeFaq === index ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  <div
                    className={`transition-all duration-500 ease-in-out overflow-hidden ${
                      activeFaq === index ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className={`px-4 py-3 ${textFaq} text-sm ${bgCard} border-t ${borderColor}`}>
                      {faq.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Still need help link */}
            <div className="mt-6 text-center">
              <p className={`${isDark ? 'text-blue-200' : 'text-gray-700'} text-sm mb-3`}>Still have questions?</p>
              <button className={`inline-flex items-center space-x-2 ${accentColor} hover:${isDark ? 'text-blue-300' : 'text-blue-500'} transition-colors duration-300 group`}>
                <span onClick={() => handleClick('contact')}>Contact support</span>
                <svg className={`w-4 h-4 group-hover:translate-x-1 transition-transform ${accentColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* CTA Section */}
      <Footer />
      
      {/* Add animation styles */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }

        .animate-fadeIn {
          animation: fadeIn 1s ease-out forwards;
        }

        .animate-slideInLeft {
          animation: slideInLeft 1s ease-out forwards;
        }

        .animate-slideInRight {
          animation: slideInRight 1s ease-out forwards;
        }

        .animate-bounce {
          animation: bounce 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default HelpSection;
